import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Search, Siren, ArrowLeft, Sparkles } from 'lucide-react';
import { haccpApi, erroreHaccp } from '../haccpApi';
import { RisultatiRicerca } from './RisultatiRicerca';
import { preparazioni } from './BadgeRichiamo';

const VUOTO = { prodotto: '', marca: '', produttore: '', fornitore: '', lotti: '', scadenze: '', formato: '', motivo: '', data_avviso: '' };
const CAMPI = [['prodotto', 'Prodotto *', 'es. Mascarpone 500 g'], ['marca', 'Marca', 'es. Galbani'], ['lotti', 'Lotti (separati da virgola)', 'es. L2345A, L2346B'],
  ['scadenze', 'Scadenze (gg/mm/aaaa, separate da virgola)', 'es. 15/11/2026'], ['fornitore', 'Fornitore', 'chi te lo ha venduto'],
  ['produttore', 'Produttore / stabilimento', ''], ['formato', 'Formato', 'es. 500 g'], ['motivo', 'Motivo del richiamo', 'es. presenza di Listeria'],
  ['data_avviso', "Data dell'avviso", 'gg/mm/aaaa']];
const elenco = (s) => s.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
const PRESELEZIONE = ['certo', 'probabile'];

const LeggiAvviso = ({ onLetto }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const carica = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await haccpApi.post('/richiami/leggi-avviso', fd);
      onLetto(r);
      if (r.dati) toast.success("Avviso letto: controlla i dati"); else toast.warning("Non sono riuscito a leggerlo: compila tu i dati");
    } catch (e) { toast.error(erroreHaccp(e, 'Avviso non caricato')); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="richiamo-avviso-btn"
      className="w-full rounded-2xl border-2 border-dashed border-red-300 bg-red-50 p-4 flex items-center gap-3 text-left disabled:opacity-60">
      {busy ? <Loader2 className="h-7 w-7 animate-spin text-red-600" /> : <Camera className="h-7 w-7 text-red-600" />}
      <div>
        <p className="font-semibold text-red-900">{busy ? "Leggo l'avviso..." : "Fotografa o carica l'avviso di richiamo"}</p>
        <p className="text-xs text-red-800">Foto o PDF: l'AI legge prodotto, lotti e scadenze. Puoi anche scriverli a mano qui sotto.</p>
      </div>
      <input ref={input} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => carica(e.target.files?.[0])} data-testid="richiamo-avviso-input" />
    </button>
  );
};

export const NuovoRichiamo = ({ onIndietro, onCreato }) => {
  const [form, setForm] = useState(VUOTO);
  const [avviso, setAvviso] = useState(null);
  const [risultati, setRisultati] = useState(null);
  const [merce, setMerce] = useState([]);
  const [prep, setPrep] = useState([]);
  const [busy, setBusy] = useState(false);
  const criteri = () => ({ ...form, lotti: elenco(form.lotti), scadenze: elenco(form.scadenze) });

  const letto = (r) => {
    setAvviso(r.avviso_id);
    if (r.dati) setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(r.dati).map(([k, v]) => [k, Array.isArray(v) ? v.join(', ') : v || f[k] || ''])) }));
  };
  const cerca = async () => {
    setBusy(true);
    try {
      const r = await haccpApi.post('/richiami/cerca', criteri());
      setRisultati(r);
      setMerce(r.merce.filter((m) => PRESELEZIONE.includes(m.livello)).map((m) => m.chiave));
      setPrep(r.preparazioni.filter((p) => PRESELEZIONE.includes(p.livello)).map((p) => p.lotto_id));
    } catch (e) { toast.error(erroreHaccp(e, 'Ricerca non riuscita')); }
    finally { setBusy(false); }
  };
  const apri = async () => {
    setBusy(true);
    try {
      const { richiamo } = await haccpApi.post('/richiami', { ...criteri(), avviso_id: avviso, merce, preparazioni: prep });
      toast.success(`Richiamo n. ${richiamo.numero} aperto${prep.length ? `: ${preparazioni(prep.length)}` : ''}`);
      onCreato(richiamo.id);
    } catch (e) { toast.error(erroreHaccp(e, 'Richiamo non aperto')); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="richiamo-nuovo">
      <button onClick={onIndietro} className="flex items-center gap-1 text-sm text-gray-600" data-testid="richiamo-nuovo-indietro"><ArrowLeft className="h-4 w-4" />Richiami</button>
      <LeggiAvviso onLetto={letto} />
      {avviso && <p className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="richiamo-avviso-allegato"><Sparkles className="h-3.5 w-3.5" />Avviso allegato al richiamo</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        {CAMPI.map(([k, label, ph]) => (
          <label key={k} className={`text-xs text-gray-600 ${k === 'motivo' ? 'sm:col-span-2' : ''}`}>{label}
            <input value={form[k]} placeholder={ph} onChange={(e) => { setForm({ ...form, [k]: e.target.value }); setRisultati(null); }}
              data-testid={`richiamo-campo-${k}`} className="mt-1 w-full h-11 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 bg-white" />
          </label>
        ))}
      </div>
      <button onClick={cerca} disabled={busy || !(form.prodotto.trim() || form.lotti.trim())} data-testid="richiamo-cerca-btn"
        className="w-full h-12 rounded-full bg-gray-900 hover:bg-gray-800 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && !risultati ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}Cerca cosa mi riguarda
      </button>
      {risultati && (
        <>
          <RisultatiRicerca risultati={risultati} merce={merce} setMerce={setMerce} prep={prep} setPrep={setPrep} />
          <button onClick={apri} disabled={busy} data-testid="richiamo-apri-btn"
            className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 sticky bottom-3">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Siren className="h-5 w-5" />}
            Apri richiamo{prep.length ? ` e blocca ${prep.length} ${prep.length === 1 ? 'preparazione' : 'preparazioni'}` : ''}
          </button>
        </>
      )}
    </div>
  );
};
